import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core'
import { of, Subscription } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { AwsEventLogEntry } from 'src/app/interfaces'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'

export interface TimeSelectCategory {
  value: number
  label: string
}
@Component({
  selector: 'app-aws-event-logs',
  templateUrl: './aws-event-logs.component.html',
  styleUrls: ['./aws-event-logs.component.scss'],
  standalone: false,
})
export class AwsEventLogsComponent implements OnInit, OnDestroy {
  @ViewChild('dt') dataTable: any
  @ViewChild('filterInput') filterInput!: ElementRef
  @Input() accountId!: string
  @Input() applicationId!: string
  @Output() close = new EventEmitter<void>()
  awsEventLogs: AwsEventLogEntry[]
  awsConsoleUrl: string
  isFilterApplied: boolean = false
  loading: boolean = true
  errorMessage: string | null = null
  firewallLogsSubscription$: Subscription

  // Dropdown time options for relative times (milliseconds)
  timeOptions: TimeSelectCategory[] = [
    { label: 'Last 5 minutes', value: 5 * 60 * 1000 },
    { label: 'Last 30 minutes', value: 30 * 60 * 1000 },
    { label: 'Last 1 hour', value: 1 * 60 * 60 * 1000 },
    { label: 'Last 12 hours', value: 12 * 60 * 60 * 1000 },
    { label: 'Last 24 hours', value: 24 * 60 * 60 * 1000 },
    { label: 'Last 3 days', value: 3 * 24 * 60 * 60 * 1000 },
    { label: 'Last 7 days', value: 7 * 24 * 60 * 60 * 1000 },
    { label: 'Last 10 days', value: 10 * 24 * 60 * 60 * 1000 },
    { label: 'Last 30 days', value: 30 * 24 * 60 * 60 * 1000 },
  ]

  // Set the default time selection to the first option (Last 5 minutes)
  timeSelected: TimeSelectCategory = this.timeOptions[0]

  constructor(
    private apiService: ApiService,
    private uiService: UiService
  ) {}

  ngOnInit(): void {
    if (this.accountId && this.applicationId) {
      // Automatically load logs for the "Last 5 minutes" on page load
      this.fetchLogs()
    } else {
      console.error('accountId or applicationId is not set')
    }
  }

  /* Method to load logs from API based on selected time range and filter pattern */
  loadFirewallLogs(
    accountId: string,
    applicationId: string,
    startTime?: number,
    endTime?: number,
    filterPattern?: string
  ) {
    this.loading = true
    this.errorMessage = null

    // Unsubscribe the previous subscription if it exists to avoid multiple calls
    if (this.firewallLogsSubscription$) {
      this.firewallLogsSubscription$.unsubscribe()
    }

    this.firewallLogsSubscription$ = this.apiService
      .fetchFirewallLogs(accountId, applicationId, 10000, startTime, endTime, filterPattern)
      .pipe(
        map((response) => {
          this.awsEventLogs = response.events.map((e) => ({
            ...e,
            timestamp: new Date(e.timestamp),
          }))
          if (this.awsEventLogs.length == 0) {
            this.errorMessage = 'No firewall events were generated in the selected time range.'
          } else {
            this.errorMessage = null
          }
          this.awsConsoleUrl = response.awsConsoleUrl
          this.loading = false
          this.uiService.sortTable(this.dataTable, 'timestamp', -1)
        }),
        catchError((error) => {
          this.errorMessage = 'Failed to load firewall events. Please try again later.'
          this.loading = false
          this.awsEventLogs = [] // Set to empty on error
          return of([])
        })
      )
      .subscribe()
  }

  // Method to handle search by time range
  fetchLogs() {
    let endTime: number | undefined = Date.now() // Get the current time in milliseconds
    let startTime: number | undefined = endTime - this.timeSelected.value // Calculate the start time based on the selected time range

    // Get filter pattern from the input field, if present
    const filterPattern = this.filterInput?.nativeElement.value.trim() || undefined

    // Load logs with the selected time range and filter pattern
    this.loadFirewallLogs(this.accountId, this.applicationId, startTime, endTime, filterPattern)
  }

  // Method to handle 'Enter' key press
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.isFilterApplied = true
      this.fetchLogs()
    }
  }

  clearFilter() {
    this.isFilterApplied = false
    this.filterInput.nativeElement.value = ''
    this.fetchLogs()
  }

  ngOnDestroy(): void {
    if (this.firewallLogsSubscription$) {
      this.firewallLogsSubscription$.unsubscribe()
    }
  }
}
